/**
 * 68.21's first step (the owner: "some issues with old orders coming back, or new orders not updating … conflicting order
 * / rejection / order precedence"; DECISIONS 152, open): the order log the dev console prints — per squad, every order
 * and reserve call the Match took or refused, who gave it (the host tags the bot commander's own: the Match stamps the
 * human and the bot commander alike as the team's seat), and what became of it: refused with its code, replaced by a later
 * order of the same tier, expired, in force now, or not in force (a task over it, or a lapse). Read-only: it reads the
 * Match's input log and the state, and changes nothing.
 */
import type { GameState, LoggedInput, SquadOrder } from '../types';
import { squadOf, squadOrderFor } from './squads';
import { squadIntent } from './squadIntent';
import { PHYS_DT } from '../constants';

/** A stamped input's key (actor and its own sequence number): how the host remembers which inputs were the bot's. */
export const inputKey = (i: Pick<LoggedInput, 'actor' | 'seq'>): string => `${i.actor}:${i.seq}`;

type OrderBody = Extract<LoggedInput['body'], { kind: 'order' }>;

/** A one-line name for an order: its kind, and what it is about when that is short. */
function orderName(o: OrderBody['order']): string {
  if ((o as { kind: string }).kind === 'free') return 'released';   // 68.27: "As you were"
  const x = o as Partial<SquadOrder> & { kind: string; pointId?: number; unitId?: number; structureId?: number; convoy?: { fobId: number; leg: 'out' | 'back' } };
  if (x.kind === 'ride' && x.convoy) return x.convoy.leg === 'out' ? `supply run to FOB #${x.convoy.fobId} (truck ${x.structureId})` : `supply run home from FOB #${x.convoy.fobId} (truck ${x.structureId})`;   // 69.4 step 5
  if (x.pointId !== undefined) return `${x.kind} ${String.fromCharCode(65 + x.pointId)}`;
  if (x.unitId !== undefined) return `${x.kind} #${x.unitId}`;
  if (x.structureId !== undefined) return `${x.kind} (body ${x.structureId})`;
  return x.kind;
}

/**
 * The log's lines for one team, the newest `perSquad` entries of each squad (a reserve call is the team's own line).
 * `botKeys`: the inputs (by `inputKey`) the host's bot commander gave.
 */
export function orderLogLines(log: readonly LoggedInput[], state: GameState, team: number, tick: number, botKeys: ReadonlySet<string>, perSquad = 6): string[] {
  const t = (k: number) => `${(k * PHYS_DT).toFixed(1)}s`;
  const who = (i: LoggedInput) => {
    const by = i.body.kind === 'order' ? i.body.by : undefined;
    if (by === 'commander' || botKeys.has(inputKey(i))) return 'commander bot';
    if (by === 'human') return 'you';
    return i.actor < 0 ? 'the team (untagged)' : `unit ${i.actor}`;
  };
  const bySquad = new Map<number | 'units' | 'reserve' | 'vehicles', Array<{ i: LoggedInput; body: OrderBody | null }>>();
  for (const i of log) {
    const b = i.body;
    if ((b.kind === 'reserves' || b.kind === 'vehicles') && b.team === team) {   // 68.25: the side's vehicles, as the reserve
      const k = b.kind === 'reserves' ? 'reserve' as const : 'vehicles' as const;
      const list = bySquad.get(k) ?? [];
      list.push({ i, body: null });
      bySquad.set(k, list);
      continue;
    }
    if (b.kind !== 'order' || b.team !== team) continue;
    const key = 'squad' in b.to ? b.to.squad : (squadOf(state, b.to.units[0])?.id ?? 'units');
    const list = bySquad.get(key) ?? [];
    list.push({ i, body: b });
    bySquad.set(key, list);
  }
  const lines: string[] = [];
  for (const [key, entries] of [...bySquad].sort((a, b) => String(a[0]).localeCompare(String(b[0])))) {
    const sq = typeof key === 'number' ? state.squads?.find(s => s.id === key) : undefined;
    // X40: what the squad is doing now (core/squadIntent.ts), the same word its tag shows, and since when
    const now = sq ? squadIntent(state, sq, tick) : undefined;
    const doing = now ? ` — now ${now.intent.replace(/([A-Z])/g, ' $1').toLowerCase()}${now.since !== undefined ? ` (since ${t(now.since)})` : ''}` : '';
    lines.push(key === 'reserve' ? 'the reserve:' : key === 'vehicles' ? 'the side\'s vehicles:' : key === 'units' ? 'units out of a squad:' : `squad ${key}:${doing}`);
    const lead = sq?.unitIds.map(id => state.players.find(p => p.id === id)).find(p => p?.alive);
    const inForce = lead ? squadOrderFor(state, lead.id, tick) : null;
    entries.forEach(({ i, body }, n) => {
      if (n < entries.length - perSquad) return;
      if (!body) {
        const b = i.body;
        const what = b.kind === 'reserves' ? `${b.call} the reserve` : b.kind === 'vehicles' ? (b.use === 'none' ? 'the side on foot' : 'vehicles free') : b.kind;
        lines.push(`  ${t(i.tick)}  ${who(i)}: ${what}${i.refused ? ` — REFUSED (${i.refused})` : ''}`);
        return;
      }
      let fate: string;
      if (i.refused) fate = `REFUSED (${i.refused})`;
      else {
        const later = entries.slice(n + 1).find(e => !e.i.refused && e.body && e.body.tier === body.tier);
        if (later) fate = `replaced at ${t(later.i.tick)} by ${who(later.i)} (${orderName(later.body!.order)})`;
        else if (body.expires !== undefined && tick >= body.expires) fate = `expired at ${t(body.expires)}`;
        else if (inForce && JSON.stringify(inForce) === JSON.stringify(body.order)) fate = 'IN FORCE';
        else fate = body.tier === 'base' ? 'not in force (a task over it, or it lapsed)' : 'not in force (it lapsed)';
      }
      const span = body.expires !== undefined ? ` until ${t(body.expires)}` : '';
      lines.push(`  ${t(i.tick)}  ${who(i)}: ${body.tier} ${orderName(body.order)}${span} — ${fate}`);
    });
  }
  lines.push(...convoyLines(state, team, t, perSquad));
  return lines.length ? lines : ['no orders yet'];
}

/**
 * 69.4 step 5 (the coordinator: "so the owner can read why a bot appeared at the base"): the side's supply — its base stock,
 * its calls for a driver at the base (the truck and FOB each was for, who was spawned by it), and its runs (driver, truck,
 * FOB, handed over, home, and how each ended), the newest `perSquad` of each.
 */
function convoyLines(state: GameState, team: number, t: (k: number) => string, perSquad: number): string[] {
  const c = state.convoys;
  if (!c) return [];
  const calls = c.calls.filter(x => x.team === team), runs = c.runs.filter(x => x.team === team);
  const lines = [`supply: the base holds ${c.stock[team] ?? 0} loads`];
  for (const x of calls.slice(-perSquad)) {
    const answer = x.unitId !== undefined ? `unit ${x.unitId} spawned at the base by it at ${t(x.spawned ?? x.tick)}` : 'waiting for a respawn';
    lines.push(`  ${t(x.tick)}  base spawn called: truck ${x.truckId} for FOB #${x.fobId} — ${answer}${x.closed !== undefined ? `; closed ${t(x.closed)}` : ''}`);
  }
  for (const r of runs.slice(-perSquad)) {
    const steps = [r.delivered !== undefined ? `handed over ${t(r.delivered)}` : null, r.home !== undefined ? `home ${t(r.home)}` : null,
      r.end ? (r.end.why === 'redeployed' ? `redeployed forward to its ${r.end.at} ${t(r.end.tick)}` : r.end.why === 'stayed' ? `stayed at the base (nowhere forward) ${t(r.end.tick)}` : `${r.end.why} ${t(r.end.tick)}`) : 'under way'].filter(Boolean);
    lines.push(`  ${t(r.out)}  run: unit ${r.unitId}${r.base ? ' (spawned at the base for it)' : ''} drove truck ${r.truckId} to FOB #${r.fobId}; ${steps.join('; ')}`);
  }
  return lines;
}
